import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin, Compass, Calendar, Store as StoreIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Home = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="w-full" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_fifa-fan-guides/artifacts/ouaihev4_Allen%20Jones.Img_6953-2.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h1 className="hero-title text-5xl sm:text-6xl lg:text-8xl text-white mb-6 leading-tight" data-testid="hero-title">
              Explore the World,<br />Fall in Love with Penticton
            </h1>
            <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
              Curated travel experiences, authentic stories, and unforgettable adventures in the heart of the Okanagan and beyond.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/vibrant-penticton">
                <Button
                  size="lg"
                  className="btn-scale bg-[#1E6FBA] hover:bg-[#1558A0] text-white rounded-full px-8 py-6 text-lg"
                  data-testid="cta-okanagan-adventure"
                >
                  Plan Your Okanagan Adventure
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/travel-stories">
                <Button
                  size="lg"
                  variant="outline"
                  className="btn-scale bg-white/90 hover:bg-white text-[#1A1A1A] border-2 border-white rounded-full px-8 py-6 text-lg"
                  data-testid="cta-travel-stories"
                >
                  Explore Travel Stories
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Core Pillars - Bento Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="core-pillars-section">
        <motion.div {...fadeInUp}>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl text-center mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Where Adventure Meets Authenticity
          </h2>
          <p className="text-center text-[#4A4A4A] text-lg mb-16 max-w-3xl mx-auto">
            From the sun-soaked beaches of Penticton to the vibrant streets of the world's greatest cities
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={stagger}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
        >
          {/* Large Feature - Vibrant Penticton */}
          <motion.div variants={fadeInUp} className="lg:col-span-2 lg:row-span-2">
            <Link to="/vibrant-penticton">
              <div className="relative h-full min-h-[400px] rounded-3xl overflow-hidden card-hover group">
                <img
                  src="https://images.pexels.com/photos/33942699/pexels-photo-33942699.jpeg"
                  alt="Vibrant Penticton"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex flex-col justify-end p-8">
                  <MapPin className="w-12 h-12 text-[#F28A30] mb-4" />
                  <h3 className="text-3xl sm:text-4xl text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Vibrant Penticton
                  </h3>
                  <p className="text-white/90 text-base mb-4 leading-relaxed">
                    Discover why Penticton is the perfect blend of adventure, relaxation, and family fun in the heart of BC wine country.
                  </p>
                  <div className="flex items-center text-white group-hover:text-[#F28A30] transition-colors">
                    <span className="font-medium">Explore the Okanagan</span>
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>

          {/* Travel Stories */}
          <motion.div variants={fadeInUp}>
            <Link to="/travel-stories">
              <div className="relative h-full min-h-[200px] rounded-3xl overflow-hidden card-hover group">
                <img
                  src="https://images.pexels.com/photos/2506923/pexels-photo-2506923.jpeg"
                  alt="Travel Stories"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex flex-col justify-end p-6">
                  <Compass className="w-10 h-10 text-[#2E8B57] mb-3" />
                  <h3 className="text-2xl text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Travel Stories
                  </h3>
                  <p className="text-white/90 text-sm mb-3">
                    Real experiences from around the globe
                  </p>
                  <div className="flex items-center text-white group-hover:text-[#2E8B57] transition-colors text-sm">
                    <span className="font-medium">Read Stories</span>
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>

          {/* Walking Tours */}
          <motion.div variants={fadeInUp}>
            <Link to="/walking-tours">
              <div className="relative h-full min-h-[200px] rounded-3xl overflow-hidden card-hover group">
                <img
                  src="https://images.pexels.com/photos/701014/pexels-photo-701014.jpeg"
                  alt="Walking Tours"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex flex-col justify-end p-6">
                  <Calendar className="w-10 h-10 text-[#1E6FBA] mb-3" />
                  <h3 className="text-2xl text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Walking Tours
                  </h3>
                  <p className="text-white/90 text-sm mb-3">
                    Guided summer experiences in Penticton
                  </p>
                  <div className="flex items-center text-white group-hover:text-[#1E6FBA] transition-colors text-sm">
                    <span className="font-medium">Sign Up</span>
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>

          {/* Store - HIDDEN */}
          {/*
          <motion.div variants={fadeInUp}>
            <Link to="/store">
              <div className="relative h-full min-h-[200px] rounded-3xl overflow-hidden card-hover group bg-[#F5E9D4] grain-texture">
                <div className="relative z-10 h-full flex flex-col justify-center items-center p-8 text-center">
                  <StoreIcon className="w-12 h-12 text-[#1E6FBA] mb-4" />
                  <h3 className="text-2xl mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Travel Merch
                  </h3>
                  <p className="text-[#4A4A4A] text-sm mb-4">
                    Travel essentials & gifts
                  </p>
                  <div className="flex items-center text-[#1E6FBA] group-hover:text-[#F28A30] transition-colors text-sm font-medium">
                    <span>Shop Now</span>
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
          */}
        </motion.div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1E6FBA] py-20" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl sm:text-5xl text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Ready to Start Your Adventure?
            </h2>
            <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
              Whether you're planning a family trip to Okanagan or seeking travel inspiration, we're here to help.
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                className="btn-scale bg-white text-[#1E6FBA] hover:bg-gray-100 rounded-full px-8 py-6 text-lg"
                data-testid="cta-get-in-touch"
              >
                Get In Touch
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
